import React, { useState } from 'react';
import { CheckIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from './Button';
import { TextField } from './forms/TextField';
import { CheckboxField } from './forms/CheckboxField';
import { SweepLoader } from './SweepLoader';
import { isValidEmail, isValidPhone } from '../utils/validation';
type SignupFormProps = {
  variant?: 'band' | 'mini';
  source?: string;
};
type Status = 'idle' | 'submitting' | 'success';

/**
 * "The Twelve Letter" signup. UI only — field names map to the CRM:
 * first_name, email, mobile, sms_consent, source.
 */
export function SignupForm({
  variant = 'band',
  source = 'twelve_letter'
}: SignupFormProps) {
  const [status, setStatus] = useState<Status>('idle');
  const [errors, setErrors] = useState<Record<string, string>>({});
  function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const data = new FormData(e.currentTarget);
    const email = String(data.get('email') ?? '').trim();
    const mobile = String(data.get('mobile') ?? '').trim();
    const sms = data.get('sms_consent') === 'on';
    const next: Record<string, string> = {};
    if (!isValidEmail(email)) next.email = 'Enter a valid email address.';
    if (variant === 'band') {
      if (mobile && !isValidPhone(mobile)) next.mobile = 'Enter a valid mobile number.';
      if (sms && !mobile) next.mobile = 'Add your mobile number to receive texts.';
    }
    setErrors(next);
    if (Object.keys(next).length) return;
    setStatus('submitting');
    window.setTimeout(() => setStatus('success'), 900);
  }
  if (status === 'success') {
    return <div role="status" className="flex items-start gap-4" data-id="element-416" data-mp="2ezalkx8fyzvl">
        <span className="grid h-10 w-10 shrink-0 place-items-center rounded-full border border-gold" data-id="element-417" data-mp="2jxc1xaehgl3d">
          <CheckIcon aria-hidden="true" strokeWidth={1.25} className="h-4 w-4 text-charcoal" data-id="element-418" data-mp="3qbbgktvwfkjs" />
        </span>
        <div data-id="element-419" data-mp="3va4vmqqu3jm2">
          <p className="t-h3 text-[1.25rem]" data-id="element-420" data-mp="3v9mov06jpmzw">You're on the list.</p>
          <p className="mt-1 text-graphite" data-id="element-421" data-mp="1v859ol5dve66">The next Twelve Letter will arrive in your inbox in the coming weeks.</p>
        </div>
      </div>;
  }
  if (variant === 'mini') {
    return <form onSubmit={handleSubmit} noValidate aria-label="Newsletter signup" data-id="element-422" data-mp="24a6294po9c1i">
        <input type="hidden" name="source" value={source} data-id="element-423" data-mp="208knfdtkunoo" />
        <div className="flex items-end gap-2" data-id="element-424" data-mp="2066rl2r2p7g2">
          <TextField label="The Twelve Letter" name="email" id={`mini-email-${source}`} type="email" autoComplete="email" placeholder="Email address" error={errors.email} className="flex-1" data-id="element-425" data-mp="1ci6yvxa7b6yu" />
          <Button type="submit" size="md" disabled={status === 'submitting'} aria-label="Subscribe" className="px-5" data-id="element-426" data-mp="1lbk49ypgc11t">
            {status === 'submitting' ? <SweepLoader label="Subscribing" data-id="element-427" data-mp="1giqw10uryakz" /> : 'Join'}
          </Button>
        </div>
      </form>;
  }
  return <form onSubmit={handleSubmit} noValidate aria-label="The Twelve Letter signup" data-id="element-428" data-mp="ssd4dbg3qdqx">
      <input type="hidden" name="source" value={source} data-id="element-429" data-mp="osvzaj4l7ima" />
      <div className="grid gap-5 md:grid-cols-3 md:items-start" data-id="element-430" data-mp="eoxlj30gb9yb">
        <TextField label="First name" name="first_name" id={`${source}-first`} autoComplete="given-name" data-id="element-431" data-mp="jfxiu9dhfu1c" />
        <TextField label="Email" name="email" id={`${source}-email`} type="email" autoComplete="email" required error={errors.email} data-id="element-432" data-mp="4r9rygel08hq" />
        <TextField label="Mobile" name="mobile" id={`${source}-mobile`} type="tel" autoComplete="tel" optional error={errors.mobile} data-id="element-433" data-mp="9r5vyfna3zxa" />
      </div>
      <div className="mt-6 flex flex-col gap-5 sm:flex-row sm:items-center sm:justify-between" data-id="element-434" data-mp="xdukhk6smqmv">
        <CheckboxField name="sms_consent" id={`${source}-sms`} data-id="element-435" data-mp="12cfl3kpyjcbu">
          Text me reading openings and new releases.
        </CheckboxField>
        <Button type="submit" size="lg" disabled={status === 'submitting'} className="w-full sm:w-auto" data-id="element-436" data-mp="sfsz2slwaf88">
          {status === 'submitting' ? <>
              <SweepLoader label="Subscribing" data-id="element-437" data-mp="38a2zabgro13g" /> Joining
            </> : 'Join the Letter'}
        </Button>
      </div>
      <p className="mt-4 max-w-[80ch] text-xs leading-relaxed text-graphite" data-id="element-438" data-mp="3ter9di99pqp7">
        By subscribing you agree to receive emails from True Twelve. If you opt in to texts, message frequency varies;
        msg &amp; data rates may apply. Reply STOP to cancel. Unsubscribe anytime. See our{' '}
        <Link to="/legal/privacy" className="underline underline-offset-2 hover:text-charcoal" data-id="element-439" data-mp="3eqebuguj7zsl">
          Privacy Policy
        </Link>
        .
      </p>
    </form>;
}